/* La mappa della linea (GI-226).
 *
 * **A che cosa risponde.** «Dove si concentrano le anomalie urgenti?» Un
 * elenco ordina per gravita', non per luogo: qui ogni sostegno sta al suo
 * posto, le campate lo legano al vicino, la traiettoria dice dov'e' passato
 * il velivolo, e ogni candidato con posizione nota porta il tono e il glifo del
 * suo rango — gli stessi della coda e dei rapporti, cosi' che un colore visto
 * qui voglia dire la stessa cosa visto la'.
 *
 * **Senza sfondo cartografico.** Nessuna tessera, nessuna richiesta di rete
 * dalla pagina oltre la lettura del servizio (ADR-002): la tela e' un SVG che
 * si disegna dalla sola risposta, in una proiezione equirettangolare corretta
 * per la latitudine — su una linea di qualche chilometro l'errore non si vede.
 *
 * **La forma dice quanto il tono.** Rombo, triangolo e doppio triangolo si
 * distinguono anche stampati in bianco e nero, e la violazione di distanza
 * porta un anello suo: e' l'unica classe che il LiDAR misura e non stima.
 *
 * **Un clic e' un vai.** Un sostegno apre la sua scheda, un'anomalia apre il
 * banco su quel candidato. Sono collegamenti, non selezioni: la mappa non
 * tiene uno stato che l'indirizzo non sappia dire.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ServiceError } from '../api'
import { marginOf, rankOf, type Rank } from '../design/scales'
import { decimal } from '../format'
import { explainService } from '../i18n/explain'
import { useT } from '../i18n/LocaleProvider'
import { tx } from '../i18n/t'
import { readLineMap, type LineMap as LineMapData, type MapElement, type MapMark, type MapPoint } from '../lineMap'
import { DRAWN, RankGlyph } from '../map/glyphs'
import { pathOf, projectionOf, SIDE } from '../map/projection'

/** Il rango con cui si disegna un candidato: il verdetto vale piu' della
 *  proposta del modello, perche' e' quello che si consegna. */
function rankOfMark(mark: MapMark): Rank {
  return rankOf(mark.final_level ?? mark.level)
}

function violates(mark: MapMark): boolean {
  const delta = marginOf(mark.measured_distance_m, mark.minimum_distance_m)
  return delta !== null && delta < 0
}

function statusWord(status: string): string {
  if (status === 'covered') return tx('coverage.statusCovered')
  if (status === 'declared') return tx('coverage.statusDeclared')
  return tx('coverage.statusGap')
}

export interface LineMapProps {
  inspectionId: string
  /** Dove va il clic su un'anomalia: se manca, al banco. Nel pannello di un
   *  elenco sceglie la riga (S17, L-3). */
  onMark?: (detectionId: string) => void
  /** Dove va il clic su un sostegno: se manca, alla sua scheda. */
  onElement?: (elementId: string) => void
  /** Senza legenda e senza riga di riepilogo: la mappa in piccolo dentro un
   *  pannello che ha gia' le sue parole. */
  bare?: boolean
}

export function LineMap({ inspectionId, onMark, onElement, bare = false }: LineMapProps): JSX.Element {
  const t = useT()
  const navigate = useNavigate()
  const [data, setData] = useState<LineMapData | null>(null)
  // Il rifiuto si conserva com'e' e si traduce al momento di scriverlo: cosi'
  // la lettura non dipende dalla lingua, e un cambio di lingua non rilegge.
  const [failure, setFailure] = useState<ServiceError | Error | null>(null)

  useEffect(() => {
    let alive = true
    setData(null)
    setFailure(null)
    readLineMap(inspectionId)
      .then((found) => {
        if (alive) setData(found)
      })
      .catch((reason: unknown) => {
        if (alive) setFailure(reason instanceof Error ? reason : new Error(String(reason)))
      })
    return () => {
      alive = false
    }
  }, [inspectionId])

  const error =
    failure === null
      ? null
      : failure instanceof ServiceError
        ? explainService(t, failure.code, failure.message)
        : t('lineMap.unreachable')

  const go = (path: string) => (event: React.KeyboardEvent | React.MouseEvent): void => {
    if ('key' in event && event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    navigate(path)
  }

  return (
    <div className="map-inline">
      <h3>{t('lineMap.title')}</h3>
      {error !== null && <p role="alert">{error}</p>}
      {data === null && error === null && <p role="status">{t('lineMap.reading')}</p>}
      {data !== null && <Drawing data={data} go={go} onMark={onMark} onElement={onElement} bare={bare} />}
    </div>
  )
}

function Drawing({
  data,
  go,
  onMark,
  onElement,
  bare,
}: {
  data: LineMapData
  go: (path: string) => (event: React.KeyboardEvent | React.MouseEvent) => void
  onMark?: (detectionId: string) => void
  onElement?: (elementId: string) => void
  bare: boolean
}): JSX.Element {
  const t = useT()
  const placed = data.elements.filter(
    (one): one is MapElement & { latitude: number; longitude: number } =>
      one.latitude !== null && one.longitude !== null,
  )
  const points: MapPoint[] = [
    ...placed.map((one): MapPoint => [one.latitude, one.longitude]),
    ...data.spans.flatMap((one) => one.points),
    ...data.trajectory,
    ...data.marks.map((one): MapPoint => [one.latitude, one.longitude]),
  ]
  if (points.length === 0) {
    return <p className="tenue">{t('lineMap.empty')}</p>
  }
  const projection = projectionOf(points)
  const base = `/inspections/${encodeURIComponent(data.inspection_id)}`

  // Un gestore proprio vince sull'indirizzo: chi lo passa vuole scegliere,
  // non andare. Il tasto si comporta come il clic.
  const pick = (handler: ((id: string) => void) | undefined, id: string, path: string) =>
    handler === undefined
      ? go(path)
      : (event: React.KeyboardEvent | React.MouseEvent): void => {
          if ('key' in event && event.key !== 'Enter' && event.key !== ' ') return
          event.preventDefault()
          handler(id)
        }
  return (
    <>
      {!bare && (
        <p className="tenue">
          {t('lineMap.summary', {
            poles: decimal(placed.length),
            marks: decimal(data.marks.length),
            points: decimal(data.trajectory_points),
          })}
        </p>
      )}
      <svg
        viewBox={`0 0 ${SIDE} ${SIDE}`}
        role="group"
        aria-label={t('lineMap.with', { poles: placed.length, marks: data.marks.length })}
        className="map-inline-canvas"
        preserveAspectRatio="xMidYMid meet"
      >
        {data.trajectory.length > 1 && (
          <path className="map-flight" d={pathOf(data.trajectory, projection)} />
        )}
        {data.spans.map((span) => (
          <path key={span.ordinal} className="map-span" d={pathOf(span.points, projection)} />
        ))}
        {placed.map((one) => {
          const x = projection.x([one.latitude, one.longitude])
          const y = projection.y([one.latitude, one.longitude])
          const worst = one.worst_level === null ? null : rankOf(one.worst_level)
          const label = t('lineMap.pole', {
            id: one.external_element_id,
            type: one.element_type,
            status: statusWord(one.status),
          })
          return (
            <g
              key={one.element_id}
              role="link"
              tabIndex={0}
              className="map-pole"
              data-status={one.status}
              aria-label={worst === null ? label : `${label}, ${t('lineMap.worst', { rank: `${worst.name} ${worst.meaning}` })}`}
              onClick={pick(onElement, one.element_id, `${base}/elements/${encodeURIComponent(one.element_id)}`)}
              onKeyDown={pick(onElement, one.element_id, `${base}/elements/${encodeURIComponent(one.element_id)}`)}
            >
              {/* Il sostegno e' un cerchio vuoto: e' un luogo, non un giudizio.
                  Con anomalie scritte il bordo prende il tono del livello
                  peggiore, e il buco di copertura si tratteggia. */}
              <circle
                cx={x}
                cy={y}
                r={1.7}
                style={worst === null ? undefined : { stroke: `var(--${worst.tone})` }}
              />
              <text x={x + 2.6} y={y - 1.4} className="map-label">
                {one.external_element_id}
              </text>
            </g>
          )
        })}
        {data.marks.map((mark) => {
          const x = projection.x([mark.latitude, mark.longitude])
          const y = projection.y([mark.latitude, mark.longitude])
          const rank = rankOfMark(mark)
          const violation = violates(mark)
          const label = t('lineMap.mark', { code: mark.code, rank: `${rank.name} ${rank.meaning}` })
          return (
            <g
              key={mark.detection_id}
              role="link"
              tabIndex={0}
              className="map-mark"
              data-confirmed={mark.confirmed ? 'true' : undefined}
              aria-label={violation ? `${label}, ${t('lineMap.violation')}` : label}
              onClick={pick(onMark, mark.detection_id, `${base}/candidates/${encodeURIComponent(mark.detection_id)}`)}
              onKeyDown={pick(onMark, mark.detection_id, `${base}/candidates/${encodeURIComponent(mark.detection_id)}`)}
            >
              {violation && <circle cx={x} cy={y} r={4.2} className="map-violation" />}
              <RankGlyph rank={rank} x={x} y={y} />
            </g>
          )
        })}
      </svg>
      {!bare && <Legend />}
    </>
  )
}

function Legend(): JSX.Element {
  const t = useT()
  return (
    <dl className="map-inline-legend">
      {DRAWN.map((rank) => (
        <div key={rank.name}>
          <dt>
            <svg viewBox="0 0 10 10" className="map-swatch" aria-hidden="true">
              <RankGlyph rank={rank} x={5} y={5} />
            </svg>
          </dt>
          <dd>
            {rank.name} · {rank.meaning}
          </dd>
        </div>
      ))}
      <div>
        <dt>
          <svg viewBox="0 0 10 10" className="map-swatch" aria-hidden="true">
            <circle cx={5} cy={5} r={4.2} className="map-violation" />
          </svg>
        </dt>
        <dd>{t('lineMap.legendViolation')}</dd>
      </div>
      <div>
        <dt>
          <svg viewBox="0 0 10 10" className="map-swatch" aria-hidden="true">
            <g className="map-pole" data-status="covered">
              <circle cx={5} cy={5} r={2.4} />
            </g>
          </svg>
        </dt>
        <dd>{t('lineMap.legendPole')}</dd>
      </div>
      <div>
        <dt>
          <svg viewBox="0 0 10 10" className="map-swatch" aria-hidden="true">
            <g className="map-pole" data-status="gap">
              <circle cx={5} cy={5} r={2.4} />
            </g>
          </svg>
        </dt>
        <dd>{t('lineMap.legendGap')}</dd>
      </div>
      <div>
        <dt>
          <svg viewBox="0 0 10 10" className="map-swatch" aria-hidden="true">
            <path className="map-span" d="M0,5 L10,5" />
          </svg>
        </dt>
        <dd>{t('lineMap.legendSpan')}</dd>
      </div>
      <div>
        <dt>
          <svg viewBox="0 0 10 10" className="map-swatch" aria-hidden="true">
            <path className="map-flight" d="M0,7 L10,3" />
          </svg>
        </dt>
        <dd>{t('lineMap.legendFlight')}</dd>
      </div>
    </dl>
  )
}
